import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /**
   * By default an icon REPLACES the label — that is what a grid/list view switch wants,
   * and the label survives as the accessible name and the tooltip. Set `showLabel` on
   * the control when the icon is illustrating an answer rather than standing in for one.
   */
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  /** Announced to screen readers, which cannot see the icons. */
  label: string;
  /**
   * `filter` is the 38px strip a screen filters itself with. `choice`
   * is the sentence-case one that answers a question in a form, where the
   * options are values rather than views and must meet the touch floor.
   *
   * `choice` also fills its row rather than shrinking to its content, so the answer
   * lines up with the inputs above and below it instead of stopping short of them.
   */
  size?: "filter" | "choice";
  /**
   * Draw the icon AND the label rather than the icon alone.
   *
   * The distinction is whether the icon is a synonym for the option or a picture of it.
   * "Grid" and "List" are views, and their icons say the whole thing. "Cock" and "Hen"
   * are values a record will carry for the bird's whole life, and a breeder filling in a
   * form should not have to recognise Mars and Venus to answer — the mark is there to
   * make the row scannable afterwards, not to replace the word.
   */
  showLabel?: boolean;
  className?: string;
}

/**
 * A small two- or three-way switch — the grid/list toggle on the bird list.
 *
 * Rendered as radios rather than buttons so the whole control is one tab stop and the
 * arrow keys move between options, which is what a screen reader user expects from a
 * choice of views. The icons are decorative; the label text is what gets announced,
 * whether or not it is also drawn.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  size = "filter",
  showLabel = false,
  className,
}: SegmentedControlProps<T>) {
  /*
   * A mark beside its word is as wide as the two together, and a segment is only a
   * third of the row. On a 360px phone "Unknown" beside its ring wants more than the
   * third it gets, and because a segment carries a min-width it shrinks under its own
   * content rather than refusing to: the padding goes first, and the raised pill on the
   * selection ends up touching the word next to it.
   *
   * Stacking makes a segment as wide as its longest single thing rather than the sum of
   * two, which is what a phone can actually give it. The row costs a few pixels of
   * height and keeps both the mark and the word at every width — no breakpoint, and
   * nothing truncated.
   */
  const stacked = size === "choice" && showLabel;

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        // A recessed pill track with a raised pill on the selection. A solid accent fill
        // here would read as the screen's primary action, which a view switch is not.
        "items-center rounded-control bg-surface-container p-1.25",
        // A filter strip is as wide as its own words; an answer in a form belongs to the
        // field, so it takes the field's width and the options share it evenly.
        size === "choice" ? "flex w-full" : "inline-flex",
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            title={option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex min-w-pill flex-1 items-center justify-center rounded-control",
              "transition-colors",
              stacked
                ? // The nav pill's icon-to-label gap, which is the same arrangement.
                  // A minimum rather than a fixed height, so a narrower phone that has
                  // to wrap a word grows the row instead of spilling out of it.
                  "min-h-touch flex-col gap-hair px-sm py-xs text-data leading-none"
                : cn(
                    "px-md",
                    // A word beside a mark needs a real gap; an icon standing alone does not.
                    showLabel ? "gap-sm" : "gap-xs",
                    size === "choice"
                      ? "h-touch text-data leading-none"
                      : "h-pill text-pill",
                  ),
              selected
                ? "bg-surface-container-lowest text-on-surface shadow-card"
                : "text-on-surface-body hover:bg-surface-container-high",
            )}
          >
            {option.icon}
            {!option.icon || showLabel ? option.label : null}
          </button>
        );
      })}
    </div>
  );
}
